import React, { useState, useEffect } from "react";
import { Button, Input, Typography, Card, Tag, Space, Tooltip, Modal, List, Row, Col } from "antd";
import { DeleteOutlined, HistoryOutlined, MoreOutlined, EyeOutlined } from "@ant-design/icons";
import useApiCall from "../common/api-call";
import PlanGraphViz from "./PlanGraphViz";

const HISTORY_STORAGE_KEY = "debug_invoke_plan_history";

const DebugInvokePlan = () => {
  const [name, setName] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [inteCapaId, setInteCapaId] = useState("");
  const [role, setRole] = useState("");
  const [version, setVersion] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [graphDialogOpen, setGraphDialogOpen] = useState(false);
  const { apiCall } = useApiCall();

  // Load history from localStorage on component mount
  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        setHistory(Array.isArray(parsedHistory) ? parsedHistory : []);
      } catch (e) {
        console.error("Error parsing history from localStorage:", e);
        setHistory([]);
      }
    }
  }, []);

  const saveToHistory = () => {
    // Create a new history entry
    const newEntry = {
      id: Date.now().toString(), // Use timestamp as unique ID
      name: name || `${tenantId}-${inteCapaId}-${role}-${version}`, // Use name if provided, otherwise create a default name
      tenantId,
      inteCapaId,
      role,
      version,
      timestamp: new Date().toISOString(),
    };

    // Check if this combination already exists to avoid duplicates
    const entryExists = history.some(
      item =>
        item.tenantId === tenantId &&
        item.inteCapaId === inteCapaId &&
        item.role === role &&
        item.version === version,
    );

    if (!entryExists) {
      // Add to history, limiting to most recent 10 entries
      const updatedHistory = [newEntry, ...history].slice(0, 10);
      setHistory(updatedHistory);

      // Save to localStorage
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(updatedHistory));
    }
  };

  const loadFromHistory = historyItem => {
    setName(historyItem.name || "");
    setTenantId(historyItem.tenantId);
    setInteCapaId(historyItem.inteCapaId);
    setRole(historyItem.role);
    setVersion(historyItem.version);
    setHistoryDialogOpen(false);
  };

  const removeHistoryItem = (e, id) => {
    e.stopPropagation(); // Prevent triggering the parent click handler
    const updatedHistory = history.filter(item => item.id !== id);
    setHistory(updatedHistory);
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(updatedHistory));
  };

  const handleDebug = async () => {
    setLoading(true);
    setResult(null);

    try {
      const endpoint = "/debug/invokePlan";
      const requestBody = {
        tenantId,
        inteCapaId,
        role,
        version,
      };
      const response = await apiCall(endpoint, "POST", requestBody, "admin");

      // Save successful queries to history
      saveToHistory();

      setResult(response);
    } catch (error) {
      console.error("Error debugging invoke plan:", error);
      setResult({ error: "Failed to debug invoke plan" });
    } finally {
      setLoading(false);
    }
  };

  // Show GraphViz visualization
  const showGraphVisualization = () => {
    setGraphDialogOpen(true);
  };

  // Format relative time for history items
  const formatRelativeTime = timestamp => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const visibleHistory = history.slice(0, 3);
  const hasMoreHistory = history.length > 3;

  return (
    <div style={{ padding: 24 }}>
      {history.length > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
            <HistoryOutlined style={{ marginRight: 8 }} />
            <Typography.Text strong>Recent Queries</Typography.Text>
          </div>
          <Space wrap>
            {visibleHistory.map(item => (
              <Tag
                key={item.id}
                closable
                onClose={e => {
                  e.preventDefault();
                  removeHistoryItem(e, item.id);
                }}
                onClick={() => loadFromHistory(item)}
                style={{ cursor: "pointer", marginBottom: 8 }}
              >
                {item.name ||
                  `${item.tenantId} / ${item.inteCapaId} / ${item.role} / ${item.version}`}
              </Tag>
            ))}
            {hasMoreHistory && (
              <Tag
                icon={<MoreOutlined />}
                onClick={() => setHistoryDialogOpen(true)}
                style={{ cursor: "pointer", marginBottom: 8 }}
              >
                More
              </Tag>
            )}
          </Space>
        </Card>
      )}

      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={6}>
            <div style={{ marginBottom: 8 }}>Name</div>
            <Input
              placeholder="Optional identifier for this debug query"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={4}>
            <div style={{ marginBottom: 8 }}>Tenant ID</div>
            <Input
              value={tenantId}
              onChange={e => setTenantId(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={4}>
            <div style={{ marginBottom: 8 }}>InteCapaId</div>
            <Input
              value={inteCapaId}
              onChange={e => setInteCapaId(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={4}>
            <div style={{ marginBottom: 8 }}>Role</div>
            <Input
              value={role}
              onChange={e => setRole(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={4}>
            <div style={{ marginBottom: 8 }}>Version</div>
            <Input
              value={version}
              onChange={e => setVersion(e.target.value)}
              style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            />
          </Col>
          <Col xs={24} sm={2}>
            <Button
              type="default"
              onClick={handleDebug}
              disabled={!tenantId || !inteCapaId || !role || !version || loading}
              loading={loading}
              style={{ marginTop: 24 }}
            >
              {loading ? "Processing..." : "Debug"}
            </Button>
          </Col>
        </Row>
      </Card>

      {result && result.plan && result.memory && (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 16, borderBottom: "1px solid #f0f0f0" }}>
            <Typography.Title level={5} style={{ margin: 0 }}>Invoke Plan Debug Result</Typography.Title>
            <Tooltip title="View Graph Visualization">
              <Button
                type="primary"
                icon={<EyeOutlined />}
                onClick={showGraphVisualization}
                size="small"
              />
            </Tooltip>
          </div>
          <div style={{ padding: 24 }}>
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Card>
                  <Typography.Title level={5}>Plan</Typography.Title>
                    <pre
                      style={{
                        whiteSpace: "pre-wrap",
                        overflow: "auto",
                        maxHeight: "400px",
                      }}
                    >
                      {result.plan || "No plan available"}
                    </pre>
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card>
                  <Typography.Title level={5}>Memory</Typography.Title>
                    <pre
                      style={{
                        whiteSpace: "pre-wrap",
                        overflow: "auto",
                        maxHeight: "400px",
                      }}
                    >
                      {result.memory || "No memory available"}
                    </pre>
                </Card>
              </Col>
            </Row>
          </div>
        </Card>
      )}

      {result && (!result.plan || !result.memory) && (
        <Card>
          <Typography.Title level={5}>Debug Result</Typography.Title>
          <pre style={{ overflow: "auto", maxHeight: "400px" }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        </Card>
      )}

      {/* History Selection Modal */}
      <Modal
        open={historyDialogOpen}
        onCancel={() => setHistoryDialogOpen(false)}
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <HistoryOutlined style={{ marginRight: 8 }} />
            Debugging History
          </div>
        }
        footer={[
          <Button key="close" onClick={() => setHistoryDialogOpen(false)}>
            Close
          </Button>,
        ]}
        width="90%"
      >
        <List
          dataSource={history}
          renderItem={item => (
            <List.Item
              actions={[
                <Button
                  key="delete"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                    onClick={e => {
                      e.stopPropagation();
                      removeHistoryItem(e, item.id);
                    }}
                />,
              ]}
              onClick={() => loadFromHistory(item)}
              style={{ cursor: "pointer" }}
              >
              <List.Item.Meta
                title={
                    item.name ||
                    `${item.tenantId} / ${item.inteCapaId} / ${item.role} / ${item.version}`
                  }
                description={`Tenant: ${item.tenantId} | InteCapaId: ${
                    item.inteCapaId
                  } | Role: ${item.role} | Version: ${
                    item.version
                  } | ${formatRelativeTime(item.timestamp)}`}
                />
            </List.Item>
          )}
        />
      </Modal>

      {/* GraphViz Visualization Modal */}
      <Modal
        open={graphDialogOpen}
        onCancel={() => setGraphDialogOpen(false)}
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <EyeOutlined style={{ marginRight: 8 }} />
              Plan Visualization
            </div>
            <Button onClick={() => setGraphDialogOpen(false)}>Close</Button>
          </div>
        }
        footer={null}
        width="100%"
        style={{ top: 0, paddingBottom: 0 }}
        bodyStyle={{ height: "calc(100vh - 64px)", padding: 0 }}
      >
          <PlanGraphViz
            planData={result?.plan || ""}
            memData={result?.memory || ""}
          />
      </Modal>
    </div>
  );
};

export default DebugInvokePlan;

